import React, { useState, useEffect, useRef } from 'react';
import { MessageFeed } from './MessageFeed.js';
import { exportConversationAsPdf } from '../utils/pdfExport.js';

export function ChatSidePanel({
  isOpen,
  onClose,
  messages,
  agentActions,
  mode,
  inputValue,
  onInputChange,
  onSubmit,
  canSend
}) {
  const DEFAULT_WIDTH = 540;
  const MIN_WIDTH = 300;
  const MAX_WIDTH = 1200;

  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const panelRef = useRef(null);
  const messagesRef = useRef(null);

  useEffect(() => {
    // Update the CSS variable so the rest of the app (like header) can reposition correctly
    document.documentElement.style.setProperty('--chat-width', isOpen ? `${width}px` : '0px');
  }, [width, isOpen]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      // Calculate width from the right edge of the screen
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Prevent text selection while dragging
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const inlineStyle = isOpen
    ? { width: `${width}px`, transition: isDragging ? 'none' : 'width 0.25s ease, border-color 0.2s ease, visibility 0.25s ease' }
    : { width: '0px' };

  return React.createElement(
    'aside',
    {
      className: 'chat-side-panel' + (isOpen ? ' open' : '') + (isDragging ? ' dragging' : ''),
      style: inlineStyle,
      ref: panelRef
    },
    // The resizer handle on the left edge
    React.createElement(
      'div',
      {
        className: 'chat-side-panel__resizer',
        onMouseDown: handleMouseDown,
        title: 'Drag to resize'
      },
      React.createElement('div', { className: 'chat-side-panel__resizer-handle' })
    ),
    React.createElement(
      'div',
      { className: 'chat-side-panel__header' },
      React.createElement('h3', null, 'Assistant Chat'),
      React.createElement(
        'div',
        { className: 'chat-side-panel__header-actions' },
        messages.length > 0
          ? React.createElement(
            'button',
            {
              type: 'button',
              className: 'chat-side-panel__download',
              disabled: exporting,
              title: 'Download conversation as PDF',
              onClick: async () => {
                if (!messagesRef.current) return;
                setExporting(true);
                try {
                  await exportConversationAsPdf(messagesRef.current);
                } catch (err) {
                  console.error('PDF export failed:', err);
                } finally {
                  setExporting(false);
                }
              }
            },
            exporting
              ? '⏳'
              : React.createElement('svg', {
                width: 16, height: 16, viewBox: '0 0 24 24',
                fill: 'none', stroke: 'currentColor', strokeWidth: 2,
                strokeLinecap: 'round', strokeLinejoin: 'round'
              },
                React.createElement('path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }),
                React.createElement('polyline', { points: '7 10 12 15 17 10' }),
                React.createElement('line', { x1: 12, y1: 15, x2: 12, y2: 3 })
              )
          )
          : null,
        React.createElement(
          'button',
          { type: 'button', className: 'chat-side-panel__close', onClick: onClose },
          '×'
        )
      )
    ),
    React.createElement('div', { ref: messagesRef, style: { overflow: 'auto', minHeight: 0 } },
      React.createElement(MessageFeed, { messages, agentActions, className: 'side-messages' })
    ),
    mode === 'text'
      ? React.createElement(
        'form',
        { className: 'chat-side-panel__composer', onSubmit },
        React.createElement('textarea', {
          value: inputValue,
          onChange: (event) => onInputChange(event.target.value),
          placeholder: 'Ask me... anything (almost).',
          rows: 3
        }),
        React.createElement(
          'button',
          { type: 'submit', className: 'chat-side-panel__send', disabled: !canSend },
          'Send'
        )
      )
      : null
  );
}
